import { CommonModule } from '@angular/common';
import { Injector, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SagaPipeModule } from '@app/pipes/module';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { MediaCapture } from '@awesome-cordova-plugins/media-capture/ngx';
import { IonicModule } from '@ionic/angular';
import { FileUploadModule, UPLOAD_CONFIGURATOR, UploadManagerService } from '@techwan/file-upload';
import { Addin, AddinModule } from '@techwan/ionic-core';
import { SharedModule } from '../../../modules/shared/shared.module';
import { CameraComponent } from './components/camera/camera.component';
import { UploadControlComponent } from './components/upload-control/upload-control.component';
import { UploadFeedbackComponent } from './components/upload-feedback/upload-feedback.component';
import { UploadItemComponent } from './components/upload-item/upload-item.component';
import { UPLOAD_FEEDBACK_TOKEN } from './schema/injection-token';
import { AttachmentProxyService } from './services/attachment-proxy/attachment-proxy.service';
import { AttachmentService } from './services/attachment/attachment.service';
import { CameraBridgeService } from './services/camera-bridge/camera-bridge.service';
import { CameraProviderService } from './services/camera-provider/camera-provider.service';
import { CaptureOptionsFactoryService } from './services/capture-options-factory/capture-options-factory.service';
import { MediaToolsSetupService } from './services/executables/media-tools-setup/media-tools-setup.service';
import { UploadsMenuSetupService } from './services/executables/uploads-menu-setup/uploads-menu-setup.service';
import { FileChooserService } from './services/file-chooser/file-chooser.service';
import { FileDelegateService } from './services/file-delegate/file-delegate.service';
import { FileReaderService } from './services/file-reader/file-reader.service';
import { MediaFileService } from './services/media-file/media-file.service';
import { MediaMessageProxyService } from './services/media-message-proxy/media-message-proxy.service';
import { MediaTextMessageService } from './services/media-text-message/media-text-message.service';
import { ThumbnailService } from './services/thumbnail/thumbnail.service';
import { UploadConfiguratorService } from './services/upload-configurator/upload-configurator.service';
import { UploadFeedbackService } from './services/upload-feedback/upload-feedback.service';
import { UploadRepositoryService } from './services/upload-repository/upload-repository.service';
import { UploadService } from './services/upload/upload.service';
import { UploadsMenuControllerService } from './services/uploads-menu-controller/uploads-menu-controller.service';
import { UploadViewComponent } from './views/upload-view/upload-view.component';

const components = [CameraComponent, UploadFeedbackComponent, UploadControlComponent, UploadItemComponent];

const views = [UploadViewComponent];

const declarations = [...components, ...views];

const services = [
  CaptureOptionsFactoryService,
  AttachmentProxyService,
  AttachmentService,
  Camera,
  CameraBridgeService,
  CameraProviderService,
  MediaCapture,
  MediaFileService,
  MediaTextMessageService,
  FileChooserService,
  FileDelegateService,
  FileReaderService,
  ThumbnailService,
  UploadConfiguratorService,
  UploadsMenuControllerService,
  UploadRepositoryService,
  UploadService,
  MediaMessageProxyService,
  { provide: UPLOAD_CONFIGURATOR, useExisting: UploadConfiguratorService },
  { provide: UPLOAD_FEEDBACK_TOKEN, useExisting: UploadFeedbackService }
];

const executables = [MediaToolsSetupService, UploadsMenuSetupService];

@Addin({
  name: 'Media'
})
@NgModule({
  declarations: [...declarations],
  providers: [...services, ...executables],
  imports: [IonicModule, FormsModule, CommonModule, SharedModule, FileUploadModule, SagaPipeModule]
})
export class MediaModule extends AddinModule {
  constructor(
    injector: Injector,
    private uploadManager: UploadManagerService,
    private uploadConfigurator: UploadConfiguratorService,
    private uploadRepository: UploadRepositoryService
  ) {
    super(injector, views, executables);
  }

  load() {
    if (!this.loaded) {
      super.load();
      this.uploadConfigurator.setup().subscribe(() => {
        this.uploadManager.init();
        this.uploadRepository.setup();
      });
    }
  }
}
